// E8ClockMaxCountClosedTask.js
// Zadanie zamkniete E8 (2025 CZE zad. 3) - obliczenia zegarowe.
// W czasie totalMin minut mieszcza sie cale nagrania po sec sekund: floor(totalMin*60 / sec).

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildData({ totalMin, sec, fixedOptions = null }) {
  const totalSec = totalMin * 60;
  const answer = Math.floor(totalSec / sec);

  let options;
  let correctIndex;
  if (fixedOptions) {
    options = fixedOptions.map((value, index) => ({ value, label: LETTERS[index] }));
    correctIndex = fixedOptions.indexOf(answer);
  } else {
    const pool = [answer, answer + 1, answer + 4, answer + 8, answer + 2, Math.ceil(totalSec / sec) + 1];
    const unique = [];
    pool.forEach((v) => {
      if (v > 0 && !unique.includes(v)) unique.push(v);
    });
    const distractors = unique.filter((v) => v !== answer).slice(0, 3);
    const ordered = shuffle([answer, ...distractors]);
    options = ordered.map((value, index) => ({ value, label: LETTERS[index] }));
    correctIndex = ordered.indexOf(answer);
  }

  return { totalMin, sec, totalSec, answer, options, correctIndex };
}

function createOriginalTask() {
  return {
    type: "E8ClockMaxCountClosedTask",
    data: buildData({ totalMin: 20, sec: 250, fixedOptions: [4, 5, 8, 12] }),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 3 },
    tags: ["e8", "obliczenia-praktyczne", "zegar", "dzielenie"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  let totalMin;
  let sec;
  let answer;
  do {
    totalMin = pick([15, 20, 25, 30]);
    sec = pick([150, 200, 220, 250, 280, 320]);
    answer = Math.floor((totalMin * 60) / sec);
  } while (answer < 3 || answer > 10 || (totalMin * 60) % sec === 0);

  return {
    type: "E8ClockMaxCountClosedTask",
    data: buildData({ totalMin, sec }),
    source: { variant: "random", basedOn: "E8_2025_CZE_3" },
    tags: ["e8", "obliczenia-praktyczne", "zegar", "dzielenie"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Martyna przez <InlineMath math={`${data.totalMin}`} /> minut słuchała kolejno
        nagrań ćwiczeń językowych. Czas odtwarzania nagrania każdego ćwiczenia w
        całości jest równy <InlineMath math={`${data.sec}`} /> sekund.
      </p>
      <p>
        Ile najwięcej ćwiczeń w całości Martyna mogła wysłuchać w tym czasie? Wybierz
        właściwą odpowiedź spośród podanych.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => (
          <div key={option.value}>
            <ChoiceOptionMath letter={LETTERS[index]} math={`${option.value}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({ label: LETTERS[index], textNode: LETTERS[index] }))}
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  return <ChoiceOptionMath letter={LETTERS[data.correctIndex]} math={`${data.answer}`} />;
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return <ChoiceOptionMath letter={LETTERS[userAnswer]} math={`${option.value}`} />;
}

export function renderExplanation({ data }) {
  const { totalMin, sec, totalSec, answer } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Krok 1.</strong> Zamieniamy czas na sekundy:</p>
      <BlockMath math={`${totalMin}\\ \\text{min}=${totalMin}\\cdot 60=${totalSec}\\ \\text{s}`} />
      <p><strong>Krok 2.</strong> Dzielimy przez czas jednego nagrania i bierzemy część całkowitą:</p>
      <BlockMath math={`${totalSec}:${sec}=${(totalSec / sec).toFixed(2).replace(".", "{,}")}`} />
      <p>
        W całości mieści się <InlineMath math={`${answer}`} /> nagrań — odpowiedź{" "}
        <InlineMath math={`\\text{${LETTERS[data.correctIndex]}}`} />.
      </p>
    </div>
  );
}
